/** @jsxImportSource @emotion/react */
import type {
  ColDef,
  ValueGetterParams,
  ValueSetterParams,
} from 'ag-grid-community'
import type { CustomCellRendererProps } from 'ag-grid-react'
import type { MenuProps } from 'antd'
import { DeleteOutlined, EditOutlined } from '@ant-design/icons'
import { t } from '@sportninja/common/i18n'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import { nodeActionLabel } from '@sportninja/common/features/competition/operations/utils/nodeActionLabels'
import { childCountKind } from '@sportninja/common/features/competition/operations/utils/childCountKind'
import {
  parseDivisionName,
  parseLockRosterDate,
  parseStandingsVisible,
} from '@sportninja/common/features/competition/operations/utils/divisionInlineEdit'
import type { OperationsScheduleNode } from '@sportninja/common/features/competition/operations/types/operations.types'
import StatusChip from 'src/components/ts-components/StatusChip'
import RowActionsCell from '../components/cells/RowActionsCell'
import type { DivisionPatch } from '../hooks/useUpdateDivisionField'
import { OPERATIONS_PILL_CELL_CLASS } from '../components/operationsGridStyle'

const translate = t as (key: string, options?: unknown) => string

export type SeasonDivisionRow = OperationsScheduleNode & {
  pending: boolean
  /** Children of this row — teams for a division, divisions for a conference. */
  childCount: number
}

export type DivisionRowAction = 'edit' | 'delete'

interface Options {
  canEdit: boolean
  /** The node whose children these rows are — names the columns. */
  parent: OperationsScheduleNode
  /** Commits one changed field on a row — see `useUpdateDivisionField`. */
  onEditField: (scheduleId: string, patch: DivisionPatch) => void
  onRowAction: (action: DivisionRowAction, row: SeasonDivisionRow) => void
}

const parseSortOrder = (
  value: unknown,
  current: number | null
): number | null => {
  const raw = String(value ?? '').trim()
  // `Number('')` is 0, so a cleared cell would otherwise commit a deliberate-looking
  // zero. An empty edit is "no change", never "set to zero".
  if (raw === '') {
    return null
  }
  const parsed = Number(raw)
  if (!Number.isInteger(parsed) || parsed < 0) {
    return null
  }
  if (parsed === current) {
    return null
  }
  return parsed
}

/**
 * A null date must read as "Not set" rather than an empty cell, so an unset
 * value is never mistaken for a loading one.
 */
const formatDate = (value: string | null): string => {
  if (!value) {
    return translate('common:notSet')
  }
  const date = new Date(value)
  return Number.isNaN(date.getTime())
    ? translate('common:notSet')
    : date.toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      })
}

/**
 * Edit / Delete for a division row. There is no "move division" flow. Labels
 * come from the row's own type — this sheet also lists conferences, which must
 * not be offered as "Delete Division".
 */
const buildDivisionRowActionItems = (
  type: OperationsScheduleNode['type']
): MenuProps['items'] => [
  {
    key: 'edit',
    icon: <EditOutlined />,
    label: capitalizeWords(nodeActionLabel('edit', type, translate)),
  },
  {
    key: 'delete',
    icon: <DeleteOutlined />,
    label: capitalizeWords(nodeActionLabel('delete', type, translate)),
    danger: true,
  },
]

/**
 * Columns for the season's divisions sheet. Same grid as the teams sheet,
 * different column config — both read from the tree payload already loaded.
 */
export const useSeasonDivisionsColumns = ({
  canEdit,
  parent,
  onEditField,
  onRowAction,
}: Options): ColDef<SeasonDivisionRow>[] => {
  'use memo'

  // What these rows are, and in turn what THEY contain — both by hierarchy
  // rule, so an empty conference still heads its count column "Divisions".
  const rowKind = childCountKind(parent)
  const rowTypeKey =
    rowKind === 'conference' ? 'common:conference' : 'common:division'
  const childCountKey =
    rowKind === 'conference' ? 'common:division_plural' : 'common:team_plural'

  const actionsColumn: ColDef<SeasonDivisionRow>[] = canEdit
    ? [
        {
          colId: 'actions',
          headerName: '',
          // Frozen to the right edge so the menu stays reachable on a wide table.
          pinned: 'right',
          width: 64,
          sortable: false,
          resizable: false,
          cellRenderer: ({
            data,
          }: CustomCellRendererProps<SeasonDivisionRow>) =>
            data ? (
              <RowActionsCell
                items={buildDivisionRowActionItems(data.type)}
                onSelect={(key) => onRowAction(key as DivisionRowAction, data)}
                label={`${data.name} actions`}
              />
            ) : null,
        },
      ]
    : []

  return [
    // No drag column: divisions and conferences are reordered by editing the
    // Order cell below, not by dragging. Only teams drag.
    {
      colId: 'sortOrder',
      field: 'sortOrder',
      headerName: capitalizeWords(translate('common:order')),
      width: 96,
      editable: canEdit,
      valueSetter: (params: ValueSetterParams<SeasonDivisionRow>) => {
        const sortOrder = parseSortOrder(params.newValue, params.data.sortOrder)
        if (sortOrder === null) {
          return false
        }
        onEditField(params.data.scheduleId, { sort_order: sortOrder })
        // Returning false keeps AG Grid from writing the row optimistically —
        // the save + tree invalidation is the single source of truth, matching
        // the roster grids.
        return false
      },
    },
    {
      colId: 'name',
      field: 'name',
      // Named for what the rows ARE — a season of conferences must not head
      // this column "Division". Same rule as the tree/stat-strip labels.
      headerName: capitalizeWords(translate(rowTypeKey)),
      // Bounded rather than open-ended: an unbounded flex here swallowed every
      // spare pixel, leaving a wide gap between the name and the counts that
      // follow it. It still grows with the viewport, just not past the point
      // where a division name needs the room.
      flex: 1,
      minWidth: 200,
      maxWidth: 420,
      editable: canEdit,
      valueSetter: (params: ValueSetterParams<SeasonDivisionRow>) => {
        const name = parseDivisionName(params.newValue, params.data.name)
        if (name === null) {
          return false
        }
        onEditField(params.data.scheduleId, { name })
        return false
      },
    },
    {
      colId: 'childCount',
      field: 'childCount',
      // A conference holds divisions, not teams, so the count column follows
      // the row type too rather than always reading "Teams".
      headerName: capitalizeWords(translate(childCountKey)),
      width: 110,
      type: 'numericColumn',
    },
    {
      colId: 'standings',
      headerName: capitalizeWords(translate('common:standings')),
      width: 130,
      editable: canEdit,
      cellEditor: 'agSelectCellEditor',
      cellEditorParams: {
        values: [translate('Web:visible'), translate('Web:hidden')],
      },
      valueSetter: (params: ValueSetterParams<SeasonDivisionRow>) => {
        const next = parseStandingsVisible(
          params.newValue,
          params.data.isVisibleStandings
        )
        if (next === null) {
          return false
        }
        onEditField(params.data.scheduleId, { is_visible_standings: next })
        return false
      },
      valueGetter: ({ data }: ValueGetterParams<SeasonDivisionRow>) =>
        data?.isVisibleStandings
          ? translate('Web:visible')
          : translate('Web:hidden'),
      cellClass: OPERATIONS_PILL_CELL_CLASS,
      cellRenderer: ({
        value,
        data,
      }: CustomCellRendererProps<SeasonDivisionRow, string>) => (
        <StatusChip tone={data?.isVisibleStandings ? 'ok' : 'muted'}>
          {value}
        </StatusChip>
      ),
    },
    {
      colId: 'lockRosterDate',
      headerName: capitalizeWords(translate('common:rosterLock')),
      // The last data column absorbs whatever is left over, so the columns
      // before it pack together and the table still fills its container. The
      // actions column after this one stays fixed — a stretched ⋯ cell would
      // just float its button in empty space.
      flex: 1,
      minWidth: 150,
      editable: canEdit,
      cellEditor: 'agDateStringCellEditor',
      valueSetter: (params: ValueSetterParams<SeasonDivisionRow>) => {
        const parsed = parseLockRosterDate(
          params.newValue,
          params.data.lockRosterDate
        )
        if (parsed === null) {
          return false
        }
        onEditField(params.data.scheduleId, { lock_roster_date: parsed.value })
        return false
      },
      // The editor needs the raw YYYY-MM-DD; the formatted label is applied
      // separately so the cell still reads "Mar 30, 2026" when not editing.
      valueGetter: ({ data }: ValueGetterParams<SeasonDivisionRow>) =>
        (data?.lockRosterDate ?? '').split(/[T ]/)[0],
      valueFormatter: ({ value }: { value?: string }) =>
        formatDate(value || null),
    },
    ...actionsColumn,
  ]
}
